/**
 * 입찰 취소 관련 유틸리티 함수들
 */

import { parseApiDateTime } from './dateUtils';

/**
 * 라이브 시작 시간 2시간 전인지 확인하는 함수
 * @param auctionStartAt 경매 시작 시간 (ISO 8601 문자열)
 * @returns 2시간 전이면 true, 아니면 false
 */
export const isWithinTwoHoursBeforeLive = (auctionStartAt: string): boolean => {
  try {
    const now = new Date();

    const auctionStart = parseApiDateTime(auctionStartAt);
    if (!auctionStart) return false;
    
    // 경매 시작 시간 2시간 전 계산
    const twoHoursBefore = new Date(auctionStart.getTime() - (2 * 60 * 60 * 1000));
    
    // 현재 시간이 2시간 전과 경매 시작 시간 사이에 있는지 확인
    return now >= twoHoursBefore && now < auctionStart;
  } catch (error) {
    console.error('Error checking auction time:', error);
    return false;
  }
};

/**
 * 입찰 취소가 가능한지 확인하는 함수
 * @param auctionStartAt 경매 시작 시간
 * @param auctionStatus 현재 경매 상태
 * @returns 입찰 취소 가능 여부
 */
export const canCancelBid = (
  auctionStartAt: string, 
  auctionStatus: 'before-submission' | 'submission-period' | 'before-live' | 'live' | 'ended'
): boolean => {
  // 시간이 파싱되지 않으면 안전을 위해 취소 불가 처리
  if (!parseApiDateTime(auctionStartAt)) {
    return false;
  }

  // 경매가 종료되었거나 라이브 상태이면 취소 불가
  if (auctionStatus === 'ended' || auctionStatus === 'live') {
    return false;
  }
  
  // 사전입찰 기간이 아니면 취소 불가
  if (auctionStatus !== 'submission-period') {
    return false;
  }
  
  // 라이브 시작 2시간 전이면 취소 불가
  if (isWithinTwoHoursBeforeLive(auctionStartAt)) {
    return false;
  }
  
  return true;
};

/**
 * 입찰 취소 불가 이유를 반환하는 함수
 * @param auctionStartAt 경매 시작 시간
 * @param auctionStatus 현재 경매 상태
 * @param language 언어 설정
 * @returns 취소 불가 이유 메시지
 */
export const getBidCancelDisabledReason = (
  auctionStartAt: string,
  auctionStatus: 'before-submission' | 'submission-period' | 'before-live' | 'live' | 'ended',
  language: 'ko' | 'en' = 'ko'
): string => {
  const rawStartAt = (auctionStartAt || '').trim();
  if (!rawStartAt) {
    return language === 'ko'
      ? '경매 시작 시간 정보가 없습니다'
      : 'Auction start time is missing';
  }

  if (!parseApiDateTime(rawStartAt)) {
    return language === 'ko'
      ? '경매 시작 시간 정보가 올바르지 않습니다'
      : 'Auction start time is invalid';
  }

  if (auctionStatus === 'ended') {
    return language === 'ko' ? '경매가 종료되었습니다' : 'Auction has ended';
  }
  
  if (auctionStatus === 'live') {
    return language === 'ko' ? '라이브 경매가 진행 중입니다' : 'Live auction is in progress';
  }
  
  if (auctionStatus !== 'submission-period') {
    return language === 'ko' ? '사전입찰 기간이 아닙니다' : 'Not in pre-bidding period';
  }
  
  if (isWithinTwoHoursBeforeLive(auctionStartAt)) {
    return language === 'ko' 
      ? '라이브 시작 2시간 전부터는 입찰 취소가 불가능합니다'
      : 'Bid cancellation is not allowed within 2 hours before live auction starts';
  }
  
  return '';
};